import { ChevronLeft, Plus } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../../design-system/button/button';
import { FileDropZone } from '../../../design-system/file-drop-zone/file-drop-zone';
import { Inline } from '../../../design-system/inline/inline';
import { Input } from '../../../design-system/input/input';
import { Stack } from '../../../design-system/stack/stack';
import { BeneficiaryView } from '../../../types/entity-types';
import { InvoiceWarningCard } from '../bill-pay-flow/components/invoice-warning-card';
import { BeneficiaryListWrapper } from '../components/beneficiary-list-wrapper';
import { getAccountDetails } from '../utils/get-account-details';
import { isSupportedCurrency } from '../utils/is-supported-currency';
import { BeneficiaryCard } from './components/beneficiary-card';
import { BeneficiaryNotFoundCard } from './components/beneficiary-not-found-card';

const MAX_BENEFICIARIES_IN_SHORT_LIST = 5;

type UploadInvoiceConfig =
  | {
      isVisible: false;
    }
  | {
      isVisible: true;
      onUploadInvoice: (files: File[]) => void;
      isUploadingInvoice: boolean;
    };

type BeneficiaryListFlowProps = {
  beneficiaries: BeneficiaryView[];
  uploadInvoiceConfig?: UploadInvoiceConfig;
  isWarningCardVisible?: boolean;
  onGoBack: () => void;
  onCreateBeneficiary: () => void;
  onSelectBeneficiary: (beneficiaryId: string) => void;
};

export const BeneficiaryListFlow = ({
  beneficiaries,
  uploadInvoiceConfig = { isVisible: false },
  isWarningCardVisible = false,
  onGoBack,
  onCreateBeneficiary,
  onSelectBeneficiary,
}: BeneficiaryListFlowProps) => {
  const [search, setSearch] = useState('');
  const [isShortList, setIsShortList] = useState(true);

  const filteredBeneficiaries = beneficiaries
    .filter((beneficiary) => {
      const searchRegex = new RegExp(search, 'i');

      return (
        searchRegex.test(beneficiary.name) ||
        (beneficiary.destination?.iban && searchRegex.test(beneficiary.destination.iban)) ||
        (beneficiary.destination?.accountNumber && searchRegex.test(beneficiary.destination.accountNumber))
      );
    })
    .sort((a, b) => a.name.localeCompare(b.name))
    .slice(0, isShortList ? MAX_BENEFICIARIES_IN_SHORT_LIST : undefined);

  return (
    <Stack space={4} align="stretch" justify="between" className="h-full">
      <Stack space={4} align="stretch" justify="between">
        <Stack space={3} align="stretch">
          <Inline justify="between">
            <Inline space={3}>
              <Button variant="ghost" size="icon" className="bg-white" onClick={onGoBack} aria-label="Go back">
                <ChevronLeft size={16} />
              </Button>

              <h2 className="text-base font-medium text-black">Select Recipient</h2>
            </Inline>

            <Button
              variant="ghost"
              className="h-10 w-10 bg-primary"
              onClick={onCreateBeneficiary}
              aria-label="Create a new beneficiary"
            >
              <Plus size={24} />
            </Button>
          </Inline>

          {isWarningCardVisible && <InvoiceWarningCard variant="DOCUMENT_UNREADABLE" />}

          <Input
            placeholder="Enter name or IBAN of the recipient"
            className="bg-white px-6 py-4 text-sm font-medium text-black"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Stack>

        <Stack space={3} align="stretch">
          <BeneficiaryListWrapper
            beneficiariesLength={filteredBeneficiaries.length}
            fallbackElement={<BeneficiaryNotFoundCard searchTerm={search} />}
          >
            {filteredBeneficiaries.map((b) => {
              const accountDetails = getAccountDetails({
                type: b.destination.type,
                accountNumber: b.destination.accountNumber,
                sortCode: b.destination.sortCode,
                iban: b.destination.iban,
              });

              if (!isSupportedCurrency(b.destination.currency)) {
                return null;
              }

              return (
                <BeneficiaryCard
                  key={b.id}
                  name={b.name}
                  currency={b.destination.currency}
                  accountDetails={accountDetails}
                  onSelectBeneficiary={() => onSelectBeneficiary(b.id)}
                />
              );
            })}
          </BeneficiaryListWrapper>

          {isShortList && beneficiaries.length > MAX_BENEFICIARIES_IN_SHORT_LIST && (
            <Button
              variant="ghost"
              size="none"
              onClick={() => {
                setIsShortList(false);

                if (search.length > 0) {
                  setSearch('');
                }
              }}
              className="w-full"
              aria-label="View all beneficiaries"
            >
              <div className="w-full rounded-3xl bg-white p-2">
                <p className="text-xs font-medium text-black">View All</p>
              </div>
            </Button>
          )}
        </Stack>
      </Stack>

      {uploadInvoiceConfig.isVisible && (
        <FileDropZone
          variant="INLINE"
          fileDescription="invoice"
          supportedFileTypes={['PDF']}
          onDropAccepted={uploadInvoiceConfig.onUploadInvoice}
          isLoading={uploadInvoiceConfig.isUploadingInvoice}
        />
      )}
    </Stack>
  );
};
